import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {UnmountClosed} from 'react-collapse';
import {Space} from 'antd';
import PropTypes from 'prop-types';
import {StyledAgentChatSuggestionTag} from './StyledAgentChatPanel';
import AgentChat from '../../../AgentChat';
import {agentWebsocketFlowConstants, agentTypeConstants} from '../../../../constants';
import {agentActions} from '../../../../state/actions/agent.actions';
import {alertActions} from '../../../../state/actions/alert.actions';
import {textHelpers, expensesHelpers} from '../../../../utils/helpers';

const AgentChatPanel = ({
  onClose,
  skipSuccessMessage = false,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [expenseSuggestions, setExpenseSuggestions] = useState([]);

  const [isAgentChatOpen, setIsAgentChatOpen] = useState(false);
  const [hideSuggestionHelpers, setHideSuggestionHelpers] = useState(false);

  const agentChatRef = useRef();

  const { agentOpen, variant, query, userName } = useSelector(({ agent: { agentChatProps }, user }) => ({
    agentOpen: agentChatProps.open,
    variant: agentChatProps.variant || agentTypeConstants.ADD_NEW_EXPENSE,
    query: agentChatProps.query || {},
    userName: textHelpers.getNameParts(user?.user?.full_name).firstName,
  }));

  const dispatch = useDispatch();

  const flowGreetingMessages = useSelector(({ agent: { flowGreetingMessages } }) => flowGreetingMessages || {});

  useEffect(() => {
    if (agentOpen && hideSuggestionHelpers) setHideSuggestionHelpers(false);
    if (agentOpen) {
      const suggestions = expensesHelpers.getExpenseSuggestions();
      setExpenseSuggestions(suggestions);
    }
  }, [agentOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectSuggestion = (suggestion) => {
    setHideSuggestionHelpers(true);
    agentChatRef?.current?.sendMessage(suggestion);
  }

  const handleRemoveSuggestion = (suggestion) => {
    expensesHelpers.removeExpenseSuggestion(suggestion);
    setExpenseSuggestions(prev => prev.filter(s => s !== suggestion));
  }

  const getSuggestionsHelper = () => {
    if (!expenseSuggestions.length) return null;

    return (
      <UnmountClosed isOpened={!hideSuggestionHelpers}>
        <Space direction='horizontal' size='small' wrap>
          {expenseSuggestions.map((suggestion) => (
            <StyledAgentChatSuggestionTag
              closable={true}
              key={suggestion}
              type='default'
              onClick={() => handleSelectSuggestion(suggestion)}
              onClose={() => handleRemoveSuggestion(suggestion)}
            >
              {textHelpers.cutString(suggestion, 40)}
            </StyledAgentChatSuggestionTag>
          ))}
        </Space>
      </UnmountClosed>
    )
  }

  const chatProps = useMemo(() => {
    const props = {
      [agentTypeConstants.ADD_NEW_BUDGET]: {
        flow: agentWebsocketFlowConstants.PROCUREMENT_REQUEST,
        title: t('agentChat.newBudget.title'),
        welcomeText: t('agentChat.newBudget.welcomeText', { userName }),
      },
      [agentTypeConstants.ADD_NEW_EXPENSE]: {
        flow: agentWebsocketFlowConstants.EXPENSE_SUBMISSION,
        footerHelper: getSuggestionsHelper(),
        title: t('agentChat.newExpense.title'),
        welcomeText: t('agentChat.newExpense.welcomeText'),
      },
      [agentTypeConstants.FILL_MISSING_INFO]: {
        flow: agentWebsocketFlowConstants.MISSING_INFO,
        footerHelper: getSuggestionsHelper(),
        title: t('agentChat.fillMissingInfo.title'),
        welcomeText: t('agentChat.fillMissingInfo.welcomeText'),
      },
    }[variant] || {};

    // update welcomeText if flowGreetingMessages has a message for the flow
    const greetingMessage = flowGreetingMessages[props.flow];
    if (greetingMessage) {
      props.welcomeText = greetingMessage;
    }
    return props;

  }, [flowGreetingMessages, variant, t, hideSuggestionHelpers, expenseSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => setIsAgentChatOpen(true);

  const handleDismiss = () => setIsAgentChatOpen(false);

  // additional effect to open/close the chat based on the agentOpen prop
  useEffect(() => {
    if (!agentOpen) {
      if (isAgentChatOpen) handleDismiss();
      return;
    }

    const flow = chatProps?.flow;
    if (flowGreetingMessages[flow]) {
      handleOpen();
      return;
    }

    dispatch(agentActions.getFlowGreetingMessage({
      flow,
      successCallback: handleOpen,
      errorCallback: handleOpen
    }));
  }, [agentOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = (props) => {
    const flow = chatProps?.flow;
    const confirmed = props?.confirmed || false;

    if (flow && confirmed && !skipSuccessMessage) {
      const alertMessage = {
        [agentWebsocketFlowConstants.MISSING_INFO]: t('agentChat.fillMissingInfo.closeSuccessText'),
        [agentWebsocketFlowConstants.EXPENSE_SUBMISSION]: t('agentChat.newExpense.closeSuccessText'),
      }[flow] || null;
      if (alertMessage) alertActions.success(alertMessage, 4000, 350);
    }

    dispatch(agentActions.closeAgentChat());
    onClose?.(props);
  }

  const handleSendMessage = ({message, messageKey}) => {
    if (messageKey !== 0) return;

    setHideSuggestionHelpers(true);
    expensesHelpers.addExpenseSuggestion(message);
  }

  return (
    <AgentChat
      open={isAgentChatOpen}
      onClose={handleClose}
      onUserMessage={handleSendMessage}
      query={query}
      ref={agentChatRef}
      {...chatProps}
      {...rest}
    />
  );
}

AgentChatPanel.propTypes = {
  onClose: PropTypes.func,
}

export default AgentChatPanel;
